import { getWorkflowOffset, OffsetUnit, workflowOffsetMinutes } from "@sp-crm/core";
import { GetWorkflowSettingsResult } from "generated/graphql";
import React, { useMemo } from "react";
import { pluralize } from "util/pluralize";
import {
    computeWorkflowDelay,
    EMAIL_OPENED_WAIT_RECIPE,
    hasEditableDelay,
    hasEmailOpenedTrigger,
    hasScheduleTrigger,
    resolveWorkflowSetting,
} from "./manage-workflows-helper";
import { EditableWorkflow } from "./types";

interface WorkflowDelaySummaryProps {
    workflow: EditableWorkflow;
    workflowSettings: GetWorkflowSettingsResult;
}

const fieldPatterns: Record<string, string> = {
    "{{client.moveTimelineActual}}": "the move-in date",
    "{{client.statusCheckLaterDate}}": "the check back later date",
    "{{homeCareLead.actualStartDate}}": "the care start date",
};

const unitLabels: Record<string, string> = {
    d: "day",
    h: "hour",
    m: "minute",
};

const describeDelayRecipe = (
    delayRecipe: string | null,
    workflow: EditableWorkflow,
    workflowSettings: GetWorkflowSettingsResult,
): string | null => {
    if (!delayRecipe) return null;

    for (const [token, label] of Object.entries(fieldPatterns)) {
        if (delayRecipe.includes(token)) {
            const fixedOffsetMatch = delayRecipe.match(/\+(\d+)([dhm])$/);
            if (fixedOffsetMatch) {
                const [, num, unit] = fixedOffsetMatch;
                const numValue = parseInt(num, 10);
                const unitName = unitLabels[unit] || unit;
                return `${num} ${pluralize(numValue, unitName)} after ${label}`;
            }

            const settingsMatch = delayRecipe.match(/\+\{\{settings\.(\w+)\}\}([dhm])$/);
            if (settingsMatch && workflowSettings) {
                const [, settingKey, unit] = settingsMatch;
                const resolvedValue = resolveWorkflowSetting(
                    workflowSettings,
                    workflow,
                    settingKey,
                );

                if (resolvedValue) {
                    const numValue = parseInt(resolvedValue, 10);
                    const unitName = unitLabels[unit] || unit;
                    return `${resolvedValue} ${pluralize(numValue, unitName)} after ${label}`;
                }

                return `After ${label} (configurable delay)`;
            }
        }
    }

    return null;
};

interface DelayAnchor {
    withoutWait: string;
    afterWait: string;
}

const TRIGGER_ANCHOR: DelayAnchor = {
    withoutWait: "Immediately",
    afterWait: "after trigger",
};

const DAILY_CHECK_ANCHOR: DelayAnchor = {
    withoutWait: "When the daily check runs",
    afterWait: "after the daily check runs",
};

const EMAIL_OPENED_ANCHOR: DelayAnchor = {
    withoutWait: "When the email is opened",
    afterWait: "after the email is opened",
};

const PREVIOUS_ACTION_ANCHOR: DelayAnchor = {
    withoutWait: "Right after the previous action",
    afterWait: "after the previous action",
};

const [BUILT_IN_EMAIL_OPENED_DELAY, BUILT_IN_EMAIL_OPENED_UNIT] = getWorkflowOffset(
    EMAIL_OPENED_WAIT_RECIPE,
);
const BUILT_IN_EMAIL_OPENED_WAIT_MINUTES = workflowOffsetMinutes(
    BUILT_IN_EMAIL_OPENED_DELAY,
    BUILT_IN_EMAIL_OPENED_UNIT,
);

const formatDelay = (delay: number, unit: OffsetUnit, anchor: DelayAnchor): string => {
    if (delay === 0) {
        return anchor.withoutWait;
    }

    const unitName = unitLabels[unit] || unit;
    return `${delay} ${pluralize(delay, unitName)} ${anchor.afterWait}`;
};

const waitBeyondBuiltInEmailOpenedWait = (
    delay: number,
    unit: OffsetUnit,
): [number, OffsetUnit] =>
    workflowOffsetMinutes(delay, unit) <= BUILT_IN_EMAIL_OPENED_WAIT_MINUTES
        ? [0, "m"]
        : [delay, unit];

const describeTriggerWait = (workflow: EditableWorkflow): string => {
    const [delay, unit] = computeWorkflowDelay(workflow);
    if (hasEmailOpenedTrigger(workflow)) {
        const [waitDelay, waitUnit] = waitBeyondBuiltInEmailOpenedWait(delay, unit);
        return formatDelay(waitDelay, waitUnit, EMAIL_OPENED_ANCHOR);
    }
    if (hasScheduleTrigger(workflow)) {
        return formatDelay(delay, unit, DAILY_CHECK_ANCHOR);
    }
    return formatDelay(delay, unit, TRIGGER_ANCHOR);
};

const lowercaseFirst = (value: string): string =>
    value.charAt(0).toLowerCase() + value.slice(1);

export const describeFixedTriggerTiming = (
    workflow: EditableWorkflow,
    verb: string,
): string | null =>
    hasEditableDelay(workflow)
        ? null
        : `${verb} ${lowercaseFirst(describeTriggerWait(workflow))}`;

export const describeActionInterval = (delayRecipe: string | null): string => {
    const [delay, unit] = computeWorkflowDelay({ delay: 0, delayRecipe });
    return formatDelay(delay, unit, PREVIOUS_ACTION_ANCHOR);
};

export const useWorkflowDelayDescription = (
    workflow: EditableWorkflow,
    workflowSettings: GetWorkflowSettingsResult,
): string | null => {
    return useMemo(() => {
        const recipeDescription = describeDelayRecipe(
            workflow.delayRecipe,
            workflow,
            workflowSettings,
        );
        if (recipeDescription) {
            return recipeDescription;
        }

        return describeTriggerWait(workflow);
    }, [workflow, workflowSettings]);
};

export const WorkflowDelaySummaryListItem: React.FC<WorkflowDelaySummaryProps> = ({
    workflow,
    workflowSettings,
}) => {
    const delayDescription = useWorkflowDelayDescription(workflow, workflowSettings);

    if (!delayDescription) {
        return null;
    }

    return (
        <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>
                <span className="font-medium">Timing:</span> {delayDescription}
            </span>
        </li>
    );
};
